/* eslint-disable no-console */
import { createApp, watch } from "vue";
import { createRouter, createWebHistory } from "vue-router";
import { createPinia } from "pinia";
import App from "./App.vue";
import { i18n } from "./i18n";
import { useAuthStore } from "./stores/auth";
import { FEATURE_ROUTES, isFeatureEnabled } from "./config/features";
import "./assets/main.scss";

// =============================================================================
// Router Configuration
// =============================================================================

const routes = [
  {
    path: "/",
    name: "home",
    component: () => import("./views/HomeView.vue"),
  },
  {
    path: "/about",
    name: "about",
    component: () => import("./views/AboutView.vue"),
  },
  {
    path: "/video",
    name: "video",
    component: () => import("./views/VideoView.vue"),
  },
  {
    path: "/video/:id",
    name: "video-detail",
    component: () => import("./views/VideoDetailView.vue"),
  },
  {
    path: "/classes",
    name: "classes",
    component: () => import("./views/ClassesView.vue"),
  },
  {
    path: "/passes",
    name: "passes",
    component: () => import("./views/PassesView.vue"),
  },
  {
    path: "/admin",
    name: "admin",
    component: () => import("./views/AdminView.vue"),
  },
  {
    path: "/bookings",
    name: "bookings",
    component: () => import("./views/BookingsView.vue"),
  },
  {
    path: "/profile",
    name: "profile",
    component: () => import("./views/ProfileView.vue"),
  },
  {
    path: "/auth/callback",
    name: "auth-callback",
    component: () => import("./views/AuthCallbackView.vue"),
    meta: { public: true },
  },
  // Catch-all route for 404
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: () => import("./views/NotFoundView.vue"),
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// =============================================================================
// Application Bootstrap
// =============================================================================

const app = createApp(App);
const pinia = createPinia();

app.use(i18n);
app.use(pinia);

// Pinia must be installed on the app before any store can be used.
const authStore = useAuthStore();

// Restore auth state from sessionStorage BEFORE installing the router.
//
// app.use(router) triggers the initial navigation (queued as a microtask).
// The first `await` after app.use(router) yields control to that microtask,
// so the beforeEach guard fires before initAuth() can restore the session —
// isAuthenticated is still false and the guard redirects to Cognito.
//
// By awaiting initAuth() first, the auth state is correct when app.use(router)
// queues the navigation, so the guard sees the live session on the first run.
await authStore.initAuth();

// =============================================================================
// Auth Guard — redirect unauthenticated users to Cognito login
// =============================================================================

// Staged rollout: routes whose feature is switched off are unreachable, even
// by typing the URL. Registered before the auth guard on purpose — sending
// someone to Cognito just to land on a disabled page would be pointless.
router.beforeEach((to) => {
  const feature = FEATURE_ROUTES[to.path];
  if (feature && !isFeatureEnabled(feature)) {
    return { path: "/", replace: true };
  }
  return true;
});

router.beforeEach(async (to) => {
  if (to.meta.public) return true;

  // VITE_SKIP_AUTH=true is set only in the axe accessibility test build so
  // Chrome headless tests our UI rather than the Cognito login page.
  // MODE !== "prod" ensures this never takes effect in production builds
  // even if the flag were accidentally passed to the build.
  if (
    import.meta.env.MODE !== "prod" &&
    import.meta.env.VITE_SKIP_AUTH === "true"
  )
    return true;

  if (!authStore.isAuthenticated) {
    try {
      await authStore.login();
    } catch {
      // login() failed (e.g. misconfigured Cognito pool) — error stored in
      // authStore.authError; let navigation proceed so the app can render it.
      return true;
    }
    // login() redirects — return false to cancel the navigation
    return false;
  }

  return true;
});

// Install the router after initAuth() so the initial navigation's guard
// pipeline always sees the restored session state.
app.use(router);

// Wait for the initial navigation to complete before mounting so RouterView
// is populated on the first render. Without this, axe (and SSR) would see
// an empty <main> because the async route component hasn't resolved yet.
await router.isReady();

// If the guard initiated a Cognito redirect (unauthenticated, non-public route,
// no auth error), skip mounting entirely to avoid flashing content before the
// browser navigates away. The VITE_SKIP_AUTH bypass used in axe tests is
// exempted so those tests still see the UI without a real session.
const skipAuth =
  import.meta.env.MODE !== "prod" && import.meta.env.VITE_SKIP_AUTH === "true";
const redirectingToCognito =
  !skipAuth &&
  !authStore.isAuthenticated &&
  !router.currentRoute.value.meta.public;

if (!redirectingToCognito) {
  // Set document title from i18n (and allow VITE_APP_NAME to override)
  const setDocumentTitle = () => {
    const title = import.meta.env.VITE_APP_NAME || i18n.global.t("app.name");
    if (title) document.title = title;
  };
  setDocumentTitle();

  // Update title when locale changes
  watch(
    () => i18n.global.locale.value,
    () => setDocumentTitle(),
  );

  // Global error handler
  app.config.errorHandler = (err, vm, info) => {
    console.error("Global error:", err);
    console.error("Component:", vm);
    console.error("Info:", info);
  };

  // Mount application
  app.mount("#app");
}

// Log startup info
console.info("Version:", __APP_VERSION__);
console.info("Tenant:", authStore.user?.tenantId ?? "(none)");
if (import.meta.env.VITE_DEBUG === "true") {
  console.info("Debug mode enabled");
}
